import { Facility, FacilityEvent, Icons, Target } from "@app/shared";
import { EventsTable, EventsTableMethods, loadEventTarget } from "@app/shared/src/components/LiveView";
import { IconLink, PageContainer } from "../../shared";
import { Pages } from "..";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialog } from "../../../hooks";
import { toastWarning } from "../../../helpers";
import { TargetDetailsDialog, getEventDialogTitle } from "./TargetDetailsDialog";

type FacilityEventsProps = {
    facility: Facility;
    onChange: () => any;
};

export const FacilityEvents: React.FC<FacilityEventsProps> = ({ facility, onChange }) => {
    const navigate = useNavigate();
    const details = useDialog<{ target: Target | undefined; event: FacilityEvent }>();
    const tableRef = useRef<EventsTableMethods>(null);

    const [downloading, setDownloading] = useState(false);

    // The export is a single limit=50000 request and can run for tens of seconds, so the button
    // has to show it's working — and stay disabled, since a second click would fire another full
    // export. The catch is load-bearing: downloadCSV rejects when that request fails (the case this
    // button is most likely to hit), and without it the failure is an unhandled rejection that
    // leaves the user staring at a spinner that just stops.
    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            await tableRef.current?.downloadCSV();
        } catch {
            toastWarning("Failed to download CSV. Please try again, or narrow the date range if the export is large");
        } finally {
            setDownloading(false);
        }
    };

    // The target is supplementary, not a precondition — loadEventTarget resolves it or returns
    // undefined, and the popup renders the event's own details either way.
    const handleViewTarget = async (event: FacilityEvent) =>
        details.show(getEventDialogTitle(event), "Okay", { target: await loadEventTarget(event), event }, () => {});

    return (
        <>
            <PageContainer
                title="Events"
                Icon={Icons.Event}
                titleRight={
                    <div className="flex items-center gap-x-4">
                        <button onClick={handleDownload} disabled={downloading} className="disabled:cursor-not-allowed disabled:opacity-60">
                            {/* pointer-events-none rather than a cursor class: IconLink hardcodes
                                cursor-pointer, and two same-specificity cursor rules would resolve by
                                stylesheet order, not by which one we passed. Killing pointer events lets
                                the button's disabled:cursor-not-allowed show through. */}
                            <IconLink
                                Icon={downloading ? Icons.Spinner : Icons.Download}
                                iconClassName={downloading ? "animate-spin" : ""}
                                className={downloading ? "pointer-events-none" : ""}
                            >
                                Download CSV
                            </IconLink>
                        </button>
                        <IconLink Icon={Icons.Chart} onClick={() => navigate(Pages.EventReports.getUrl())}>Reports</IconLink>
                    </div>
                }
            >
                <EventsTable
                    ref={tableRef}
                    mode="facility"
                    facility={facility}
                    devices={facility.devices || []}
                    facilityId={facility.id}
                    timezoneId={facility.localTimezoneId}
                    onRowClick={(event) => handleViewTarget(event)}
                    enableDelete
                />
            </PageContainer>
            {details.renderDialog(
                (val) => <TargetDetailsDialog facility={facility} target={val?.target} event={val?.event} showVideoSection />,
                { closeOnOutsideClick: true, showCancelButton: false, className: "w-full max-w-3xl max-h-[95vh] overflow-y-auto" }
            )}
        </>
    );
};
